
/**
 * Incoming invoices (Eingangsrechnungen) — models the legacy `incoming_invoices` workflow: capture
 * → Kontierung → role-gated Fachfreigabe → role-gated Zahlungsfreigabe → paid. Tax is normalised
 * from the net amount and rate (0/7/19), like the legacy server trigger. OCR capture (pdfjs +
 * tesseract in the legacy) is a later add-on; here capture is manual. Gets the FORQ Flow trail.
 */
export type IncomingInvoiceStatus =
  | 'captured' // Erfassung
  | 'pre_booked' // Vorkontiert
  | 'review_requested' // Zur Fachfreigabe
  | 'approved' // Fachfreigegeben
  | 'payment_requested' // Zur Zahlungsfreigabe
  | 'payment_released' // Zahlung freigegeben
  | 'paid' // Bezahlt
  | 'rejected' // Abgelehnt
  | 'cancelled'; // Storniert

export type ReviewerRole = 'serviceleitung' | 'betriebsleitung' | 'verwaltung' | 'geschaeftsfuehrung';

export type IncomingTaxRate = 0 | 7 | 19;

export interface IncomingInvoice {
  readonly id: string;
  readonly tenantId: string;
  /** Internal capture number (ER-26-NNNNNN). */
  readonly number: string;
  readonly supplierNumber: string;
  readonly supplierName: string;
  /** The supplier's own invoice number. */
  readonly supplierInvoiceNumber: string;
  readonly invoiceDate: string;
  readonly dueDate: string | null;
  readonly netAmount: number;
  readonly taxRate: IncomingTaxRate;
  readonly taxAmount: number;
  readonly grossAmount: number;
  readonly status: IncomingInvoiceStatus;
  // Kontierung
  readonly accountNumber: string;
  readonly costCenter: string;
  readonly bookingText: string;
  readonly reviewerRole: ReviewerRole | '';
  readonly note: string;
  readonly decisionNote: string;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export interface CreateIncomingInvoiceInput {
  readonly tenantId: string;
  readonly supplierNumber: string;
  readonly supplierName: string;
  readonly supplierInvoiceNumber: string;
  readonly invoiceDate: string;
  readonly dueDate?: string | null;
  readonly netAmount: number;
  readonly taxRate: IncomingTaxRate;
  readonly note?: string;
}

export interface UpdateAccountingInput {
  readonly tenantId: string;
  readonly id: string;
  readonly accountNumber: string;
  readonly costCenter: string;
  readonly bookingText: string;
  readonly reviewerRole: ReviewerRole | '';
}

export interface TransitionIncomingInput {
  readonly tenantId: string;
  readonly id: string;
  readonly toStatus: IncomingInvoiceStatus;
  readonly note?: string;
}

export interface ListIncomingInput {
  readonly tenantId: string;
  readonly status?: IncomingInvoiceStatus | 'all';
}

export interface IncomingInvoiceGateway {
  listIncomingInvoices(input: ListIncomingInput): Promise<readonly IncomingInvoice[]>;
  createIncomingInvoice(input: CreateIncomingInvoiceInput): Promise<IncomingInvoice>;
  updateAccounting(input: UpdateAccountingInput): Promise<IncomingInvoice>;
  transitionIncoming(input: TransitionIncomingInput): Promise<IncomingInvoice>;
}

/**
 * Approval workflow graph — single source for the demo gateway, a future Supabase guard and the
 * FORQ Flow controls. Mirrors the legacy forq_transition_incoming_invoice state machine.
 */
export const incomingTransitions: Readonly<Record<IncomingInvoiceStatus, readonly IncomingInvoiceStatus[]>> = {
  captured: ['pre_booked', 'cancelled'],
  pre_booked: ['review_requested', 'cancelled'],
  review_requested: ['approved', 'rejected'],
  approved: ['payment_requested', 'cancelled'],
  payment_requested: ['payment_released', 'rejected'],
  payment_released: ['paid'],
  paid: [],
  rejected: ['pre_booked', 'cancelled'],
  cancelled: [],
};

export function allowedIncomingTransitionsFor(status: IncomingInvoiceStatus): readonly IncomingInvoiceStatus[] {
  return incomingTransitions[status];
}

export const incomingStatusLabels: Readonly<Record<IncomingInvoiceStatus, string>> = {
  captured: 'Erfassung',
  pre_booked: 'Vorkontiert',
  review_requested: 'Zur Fachfreigabe',
  approved: 'Fachfreigegeben',
  payment_requested: 'Zur Zahlungsfreigabe',
  payment_released: 'Zahlung freigegeben',
  paid: 'Bezahlt',
  rejected: 'Abgelehnt',
  cancelled: 'Storniert',
};

/** Button label for the action that reaches the given status. */
export const incomingActionLabels: Readonly<Record<IncomingInvoiceStatus, string>> = {
  captured: 'Zurück zur Erfassung',
  pre_booked: 'Vorkontieren',
  review_requested: 'Zur Fachfreigabe',
  approved: 'Fachlich freigeben',
  payment_requested: 'Zur Zahlungsfreigabe',
  payment_released: 'Zahlung freigeben',
  paid: 'Als bezahlt buchen',
  rejected: 'Ablehnen',
  cancelled: 'Stornieren',
};

/** Permission accepted to move an incoming invoice INTO the given status (payment release is restricted). */
export const incomingTransitionPermission: Readonly<Record<IncomingInvoiceStatus, string>> = {
  captured: 'finance.incoming.edit',
  pre_booked: 'finance.incoming.edit',
  review_requested: 'finance.incoming.edit',
  approved: 'finance.incoming.review',
  rejected: 'finance.incoming.review',
  payment_requested: 'finance.incoming.edit',
  payment_released: 'finance.payments.approve',
  paid: 'finance.payments.record',
  cancelled: 'finance.incoming.edit',
};

export const reviewerRoleLabels: Readonly<Record<ReviewerRole, string>> = {
  serviceleitung: 'Serviceleitung',
  betriebsleitung: 'Betriebsleitung',
  verwaltung: 'Verwaltung',
  geschaeftsfuehrung: 'Geschäftsführung',
};

/** Normalises tax + gross from net × rate (legacy: server trigger forq_normalize_incoming_invoice_tax). */
export function normalizeIncomingTax(netAmount: number, taxRate: IncomingTaxRate): { taxAmount: number; grossAmount: number } {
  const taxAmount = Math.round(netAmount * (taxRate / 100) * 100) / 100;
  return { taxAmount, grossAmount: Math.round((netAmount + taxAmount) * 100) / 100 };
}

export interface IncomingTransition {
  readonly toStatus: IncomingInvoiceStatus;
  readonly label: string;
  readonly permission: string;
  readonly tone: 'default' | 'danger';
}

/** The next workflow steps the user is permitted to perform from the invoice's current status. */
export function incomingFlowTransitions(invoice: IncomingInvoice, permissions: ReadonlySet<string>): readonly IncomingTransition[] {
  return allowedIncomingTransitionsFor(invoice.status)
    .map((toStatus) => ({
      toStatus,
      label: incomingActionLabels[toStatus],
      permission: incomingTransitionPermission[toStatus],
      tone: toStatus === 'rejected' || toStatus === 'cancelled' ? ('danger' as const) : ('default' as const),
    }))
    .filter((transition) => permissions.has(transition.permission));
}
